/**
 * DataChangesMap — precinct-level choropleth-style tile grid showing where
 * voter-roll changes are concentrated for a given import batch.
 *
 * Slotted into `DataChangesView.tsx` under "Where the changes are
 * concentrated." The view's container component handles scrolling/layout;
 * this map is fixed at ~400px tall and is mobile-friendly.
 *
 * --- On the choice of "tile grid" instead of a true geographic choropleth ---
 *
 * The codebase has no precinct GeoJSON shipped (only PDF map series in
 * `public/maps/`), and `MapView.tsx` itself does not render geographic
 * geometry — it lays precincts out as a tiled subregion grid driven by the
 * tenant's turf catalog (`GET /geo/regions`). To keep the visual idiom
 * consistent with the rest of the app and avoid inventing geometry that
 * doesn't exist, this component reuses the same grouping: each subregion
 * is a column-flex group of precinct tiles, colored on a single-hue blue
 * ramp by `voters_modified`. If/when real precinct GeoJSON lands the inner
 * tile renderer can be swapped for a Leaflet `<GeoJSON>` layer (see
 * `src/components/InteractiveMap.tsx` for the existing Leaflet wiring).
 *
 * Patterned on:
 *   - `MapView.tsx` (precinct → turf-catalog join, by-precinct fetch)
 *   - `EngagementHeatmapView.tsx` (loading / error / empty state shape +
 *     red retry card)
 *   - `dataChanges.ts` (axios `client` + auth-header pattern)
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Map as MapIcon } from 'lucide-react';
import client from '@/api/client';
import { useMapRegions } from '@/context/MapRegionsContext';
import type { MapRegionCatalog } from '@/api/geoRegions';
import { Skeleton } from '@/components/Skeleton';
import EmptyState from '@/components/EmptyState';

// ---------------------------------------------------------------------------
// Local types — backend Pydantic schema lives in the API agent's domain;
// the frontend type stays declared here per the task contract.
// ---------------------------------------------------------------------------

interface PrecinctDeltaCount {
  precinct_num: string;
  precinct_name: string | null;
  voters_modified: number;
  voters_added: number;
  voters_removed: number;
  total_deltas: number;
}

interface DataChangesMapProps {
  importBatchId: string;
  /** Optional: scope the "modified" count to a single field (e.g. 'status'). */
  field?: string | null;
}

// ---------------------------------------------------------------------------
// Color ramp — single-hue blue, anchored to design-system CSS vars where
// available, with hardcoded fallbacks. The 0 bucket uses --bg-elevated so
// "no changes" tiles blend into the dark card background.
// ---------------------------------------------------------------------------

interface RampStop {
  /** Lower bound (inclusive) of normalized 0..1 score. */
  lo: number;
  /** Hex / CSS color used as `backgroundColor`. */
  bg: string;
  /** Foreground text color for legibility on this bg. */
  fg: string;
}

const RAMP: RampStop[] = [
  { lo: 0, bg: 'var(--bg-elevated, #0f172a)', fg: '#94a3b8' },
  { lo: 0.001, bg: '#1e3a8a33', fg: '#cbd5e1' }, // very light blue tint
  { lo: 0.2, bg: '#1e40af66', fg: '#e2e8f0' },
  { lo: 0.4, bg: '#2563eb99', fg: '#f8fafc' },
  { lo: 0.6, bg: '#2563ebcc', fg: '#ffffff' },
  { lo: 0.8, bg: 'var(--chart-series-2, #3b82f6)', fg: '#ffffff' },
];

function rampColor(score: number): RampStop {
  if (!Number.isFinite(score) || score <= 0) return RAMP[0];
  let pick = RAMP[0];
  for (const stop of RAMP) {
    if (score >= stop.lo) pick = stop;
  }
  return pick;
}

// ---------------------------------------------------------------------------
// Subregion layout.
//
// This used to be five hand-curated rows of literal Platte turf keys
// ('North' → ['13-4'], and so on), copied from MapView. That grouping was a
// real cartographic judgement for Platte and nonsense anywhere else: on any
// other tenant every key missed and the map rendered as an empty grid of
// spacers. Rows are grouped by legislative district instead — a fact the
// catalog carries for every tenant — and turfs stay in the server's display
// order inside each row, which is the tenant-editable ordering their
// coordinators actually think in.
// ---------------------------------------------------------------------------

function subregionRows(
  regions: MapRegionCatalog,
): { name: string; srs: string[] }[] {
  const byLd = new Map<string, string[]>();
  for (const key of regions.keys) {
    const ld = String(regions.byKey[key].ld);
    const bucket = byLd.get(ld);
    if (bucket) bucket.push(key);
    else byLd.set(ld, [key]);
  }
  return regions.ldOptions
    .filter((ld) => byLd.has(ld))
    .map((ld) => ({ name: `LD ${ld}`, srs: byLd.get(ld) as string[] }));
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function DataChangesMap({
  importBatchId,
  field,
}: DataChangesMapProps) {
  const { catalog: regions, loading: regionsLoading } = useMapRegions();
  const [rows, setRows] = useState<PrecinctDeltaCount[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [hovered, setHovered] = useState<PrecinctDeltaCount | null>(null);

  const fetchData = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params: Record<string, string> = {
      import_batch_id: importBatchId,
    };
    if (field) params.field = field;

    // ----- Real endpoint -----
    client
      .get<PrecinctDeltaCount[]>('/admin/data-changes/by-precinct', { params })
      .then((res) => {
        if (cancelled) return;
        setRows(Array.isArray(res.data) ? res.data : []);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        const code = err?.response?.status;
        if (code === 401) {
          setError('Not authenticated — log in to see data changes.');
        } else if (code === 403) {
          setError('You need ADMIN access to see this map.');
        } else {
          setError(
            err?.response?.data?.detail ||
              err?.message ||
              'Failed to load precinct change counts.',
          );
        }
        setLoading(false);
      });

    // ----- MOCK FALLBACK (REMOVE ONCE BACKEND IS LIVE) -----------------
    // The /admin/data-changes/by-precinct endpoint is being added in
    // parallel. If it 404s during local dev, uncomment the block below
    // to verify rendering. DELETE these comments + this block once the
    // backend ships and you have confirmed the live response.
    //
    // Promise.resolve()
    //   .then(() => {
    //     if (cancelled) return;
    //     const mock: PrecinctDeltaCount[] = Object.values(regions.byKey)
    //       .flatMap((r: any) => (r.precincts || []) as number[])
    //       .map((n) => ({
    //         precinct_num: String(n),
    //         precinct_name: `Precinct ${n}`,
    //         voters_modified: Math.floor(Math.random() * 250),
    //         voters_added: Math.floor(Math.random() * 30),
    //         voters_removed: Math.floor(Math.random() * 15),
    //         total_deltas: 0,
    //       }))
    //       .map((r) => ({
    //         ...r,
    //         total_deltas: r.voters_modified + r.voters_added + r.voters_removed,
    //       }));
    //     setRows(mock);
    //     setLoading(false);
    //   });
    // -------------------------------------------------------------------

    return () => {
      cancelled = true;
    };
  }, [importBatchId, field]);

  useEffect(() => {
    const cleanup = fetchData();
    return cleanup;
  }, [fetchData, reloadKey]);

  // -----------------------------------------------------------------------
  // Derived: precinct lookup + max value for ramp normalization
  // -----------------------------------------------------------------------
  const { byPrecinct, maxModified } = useMemo(() => {
    if (!rows) {
      return {
        byPrecinct: new Map<string, PrecinctDeltaCount>(),
        maxModified: 0,
      };
    }
    const m = new Map<string, PrecinctDeltaCount>();
    let max = 0;
    for (const row of rows) {
      m.set(String(row.precinct_num), row);
      if (row.voters_modified > max) max = row.voters_modified;
    }
    return { byPrecinct: m, maxModified: max };
  }, [rows]);

  // The tile grid's rows, one per legislative district in the turf catalog.
  const srRows = useMemo(() => subregionRows(regions), [regions]);

  // -----------------------------------------------------------------------
  // Loading
  // -----------------------------------------------------------------------
  if ((loading && !rows) || regionsLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="w-full" h="400px" />
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Error — mirrors EngagementHeatmapView's red retry card
  // -----------------------------------------------------------------------
  if (error) {
    return (
      <div
        className="bg-status-danger-bg/20 border border-status-danger-bg/30 rounded-lg p-6"
        role="alert"
        data-testid="data-changes-map-error"
      >
        <p className="text-sm font-semibold text-status-danger">
          Couldn't load the changes map
        </p>
        <p className="text-xs text-status-danger/80 mt-1">{error}</p>
        <button
          type="button"
          onClick={() => setReloadKey((k) => k + 1)}
          className="mt-4 inline-flex items-center min-h-[44px] px-4 py-2 bg-accent hover:bg-accent-hover rounded text-white text-sm font-semibold focus:outline-none"
        >
          Retry
        </button>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Empty
  // -----------------------------------------------------------------------
  // Two distinct emptinesses. No delta rows means the importer had nothing to
  // say about this batch; no turfs means the map has no grid to draw them on,
  // which is a tenant-configuration gap and needs to say so rather than blame
  // the importer.
  if (!rows || rows.length === 0) {
    return (
      <EmptyState
        icon={MapIcon}
        title="No changes mapped for this batch."
        description="Once the importer reports per-precinct deltas they'll show up here."
      />
    );
  }

  if (srRows.length === 0) {
    return (
      <EmptyState
        icon={MapIcon}
        title="Turfs not yet configured."
        description="This map groups precinct changes by turf. Once this tenant's turfs are loaded the batch's deltas will appear here."
      />
    );
  }

  // -----------------------------------------------------------------------
  // Happy path — tile grid
  // -----------------------------------------------------------------------
  return (
    <div
      className="bg-bg-raised rounded-xl border border-border-base p-4"
      data-testid="data-changes-map"
    >
      {/* Body: scrollable so it stays at ~400px on tall datasets */}
      <div
        className="overflow-y-auto"
        style={{ maxHeight: 360 }}
        aria-label="Precinct map of voter-roll changes"
      >
        <div className="space-y-3">
          {srRows.map((row) => (
            <div key={row.name}>
              <div className="text-[11px] uppercase font-semibold text-fg-muted mb-1">
                {row.name}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                {row.srs.map((srCode) => {
                  const sr = regions.byKey[srCode];
                  if (!sr) return <div key={srCode} />;
                  return (
                    <SubregionTile
                      key={srCode}
                      code={srCode}
                      name={sr.name}
                      precincts={sr.precinctsAsStrings}
                      byPrecinct={byPrecinct}
                      maxModified={maxModified}
                      onHover={setHovered}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer: legend + active-tooltip line */}
      <div className="mt-4 pt-3 border-t border-border-base flex flex-wrap items-center gap-3 justify-between">
        <Legend max={maxModified} field={field} />
        <div
          className="text-xs text-fg-secondary min-h-[1.25rem]"
          aria-live="polite"
        >
          {hovered ? (
            <span>
              <span className="font-semibold text-fg-primary">
                {hovered.precinct_name || `Precinct ${hovered.precinct_num}`}
              </span>{' '}
              <span className="text-fg-muted">
                · {hovered.voters_modified.toLocaleString()} modified ·{' '}
                {hovered.voters_added.toLocaleString()} added ·{' '}
                {hovered.voters_removed.toLocaleString()} removed
              </span>
            </span>
          ) : (
            <span className="text-fg-muted">
              Hover or tap a precinct for details
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SubregionTile — one tile per subregion, with a mini-grid of precinct chips
// inside it. Each chip is colored by its `voters_modified` count.
// ---------------------------------------------------------------------------

interface SubregionTileProps {
  code: string;
  name: string;
  precincts: string[];
  byPrecinct: Map<string, PrecinctDeltaCount>;
  maxModified: number;
  onHover: (row: PrecinctDeltaCount | null) => void;
}

function SubregionTile({
  code,
  name,
  precincts,
  byPrecinct,
  maxModified,
  onHover,
}: SubregionTileProps) {
  const subregionTotal = precincts.reduce(
    (sum, p) => sum + (byPrecinct.get(p)?.voters_modified ?? 0),
    0,
  );

  return (
    <div className="bg-bg-base rounded-lg border border-border-base p-2">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[11px] font-bold text-fg-primary">{code}</div>
        <div
          className="text-[10px] text-fg-muted tabular-nums"
          title={`${subregionTotal.toLocaleString()} voters modified in ${code}`}
        >
          {subregionTotal.toLocaleString()}
        </div>
      </div>
      <div className="text-[10px] text-fg-muted truncate mb-1.5" title={name}>
        {name}
      </div>
      <div className="flex flex-wrap gap-1">
        {precincts.map((p) => (
          <PrecinctChip
            key={p}
            precinctNum={p}
            row={byPrecinct.get(p) ?? null}
            maxModified={maxModified}
            onHover={onHover}
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PrecinctChip — colored square per precinct.
// ---------------------------------------------------------------------------

interface PrecinctChipProps {
  precinctNum: string;
  row: PrecinctDeltaCount | null;
  maxModified: number;
  onHover: (row: PrecinctDeltaCount | null) => void;
}

function PrecinctChip({
  precinctNum,
  row,
  maxModified,
  onHover,
}: PrecinctChipProps) {
  const score = row && maxModified > 0 ? row.voters_modified / maxModified : 0;
  const stop = rampColor(score);

  // Synthesize a "no data" row so hover shows zero counts rather than a
  // confusing blank line.
  const hoverRow: PrecinctDeltaCount =
    row ?? {
      precinct_num: precinctNum,
      precinct_name: `Precinct ${precinctNum}`,
      voters_modified: 0,
      voters_added: 0,
      voters_removed: 0,
      total_deltas: 0,
    };

  const tooltip =
    `${hoverRow.precinct_name || `Precinct ${precinctNum}`}\n` +
    `Modified: ${hoverRow.voters_modified.toLocaleString()}\n` +
    `Added: ${hoverRow.voters_added.toLocaleString()}\n` +
    `Removed: ${hoverRow.voters_removed.toLocaleString()}`;

  return (
    <button
      type="button"
      data-testid="data-changes-precinct"
      data-precinct={precinctNum}
      title={tooltip}
      aria-label={`Precinct ${precinctNum}: ${hoverRow.voters_modified.toLocaleString()} voters modified`}
      onMouseEnter={() => onHover(hoverRow)}
      onMouseLeave={() => onHover(null)}
      onFocus={() => onHover(hoverRow)}
      onBlur={() => onHover(null)}
      onClick={() => onHover(hoverRow)}
      className="relative inline-flex items-center justify-center rounded text-[10px] font-semibold border border-border-base/80 cursor-pointer focus:outline-none focus:ring-2 focus:ring-status-info hover:ring-1 hover:ring-status-info transition"
      style={{
        backgroundColor: stop.bg,
        color: stop.fg,
        width: 28,
        height: 28,
      }}
    >
      <span className="tabular-nums">{precinctNum}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Legend
// ---------------------------------------------------------------------------

function Legend({ max, field }: { max: number; field?: string | null }) {
  const label = field ? `${field} changes` : 'voters modified';
  return (
    <div className="flex items-center gap-2">
      <span className="text-[11px] text-fg-muted tabular-nums">0</span>
      <div className="flex h-3 w-32 rounded overflow-hidden border border-border-base">
        {RAMP.map((s) => (
          <div
            key={s.lo}
            className="flex-1"
            style={{ backgroundColor: s.bg }}
            aria-hidden="true"
          />
        ))}
      </div>
      <span className="text-[11px] text-fg-muted tabular-nums">
        {max.toLocaleString()}
      </span>
      <span className="text-[11px] text-fg-muted ml-1">{label}</span>
    </div>
  );
}
